// PLAN-cockpit-v2-iter1 Phase 1: Shell unification
// AppHeader + SelectedItemProvider + AppShell detail slot 復活 + drag resize

import { useCallback, useEffect, useState } from 'react'
import { ThemeProvider } from './theme'
import { V2RouterProvider, useV2Router, LENS_ORDER, type LensId } from './router'
import { ToastProvider } from './components/common/Toast'
import { SelectedItemProvider, useSelectedItem } from './hooks/useSelectedItem'
import { useDetailVisibility } from './hooks/useDetailVisibility'
import { usePanelResize } from './hooks/usePanelResize'
import { useSubSidebarResize } from './hooks/useSubSidebarResize'
import { AppShell } from './components/AppShell'
import { MobileAppShell } from './components/MobileAppShell'
import { AppHeader } from './components/AppHeader'
import { LensNav } from './components/LensNav'
import { BottomNav } from './components/BottomNav'
import { MobileDetailModal } from './components/MobileDetailModal'
import { CommandPalette } from './components/CommandPalette'
import { SharedDetailPanel } from './components/SharedDetailPanel'
import { PermalinkView } from './components/PermalinkView'
import { useViewport } from './hooks/useViewport'
import { HomeLens } from './lenses/Home'
import { ExploreLens, ExploreSidebar } from './lenses/Explore'
import { ReflectLens, ReflectSidebar } from './lenses/Reflect'
import { CollaborateLens, CollaborateSidebar } from './lenses/Collaborate'
import { GraphLens, GraphSidebar } from './lenses/Graph'
import { FinanceLens, FinanceSidebar } from './lenses/Finance'
import { HealthLens, HealthSidebar } from './lenses/Health'
import { MindLens, MindSidebar } from './lenses/Mind'
import { JudgmentLens, JudgmentSidebar } from './lenses/Judgment'

// Curate Lens は iter1 Phase 2 で route 廃止 (home redirect)、Phase 5 (2026-05-11) で実装完全削除。
// 機能は Detail Panel edit mode + DomainOverlaySection に統合済 (SPEC §5.3.2)

interface AppV2Props {
  onUnauthorized: () => void
}

export function AppV2({ onUnauthorized }: AppV2Props) {
  return (
    <ThemeProvider>
      <V2RouterProvider>
        <ToastProvider>
          <SelectedItemProvider>
            <V2Switch onUnauthorized={onUnauthorized} />
          </SelectedItemProvider>
        </ToastProvider>
      </V2RouterProvider>
    </ThemeProvider>
  )
}

// PLAN-permalink-cockpit-convergence Phase D: permalink route は Lens shell を構築せず
// PermalinkView をフルスクリーン表示する。lens default detail state はトリガーしない。
function V2Switch({ onUnauthorized }: AppV2Props) {
  const { route } = useV2Router()
  if (route.kind === 'permalink') {
    return <PermalinkView id={route.id} />
  }
  return <V2Body onUnauthorized={onUnauthorized} />
}

function V2Body({ onUnauthorized }: AppV2Props) {
  const { lens, navigate } = useV2Router()
  const { selectedItem, setSelectedItem } = useSelectedItem()
  const { visible: lensDefaultVisible } = useDetailVisibility(lens)
  const { width: detailWidth, onDragHandleMouseDown, reset: resetDetailWidth } = usePanelResize()
  const { width: subSidebarWidth, onDragHandleMouseDown: onSubSidebarResizeStart, reset: resetSubSidebarWidth } = useSubSidebarResize()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const viewport = useViewport()

  // selectedItem がある時は常に Detail 表示、なければ Lens default
  const detailVisible = !!selectedItem || lensDefaultVisible

  // Lens 切替時に selectedItem をクリア
  useEffect(() => {
    setSelectedItem(null)
  }, [lens, setSelectedItem])

  // ⌘K + Cmd+0〜4 ショートカット (Curate 廃止で Cmd+3=Collab に変更は Phase 2)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (meta && e.key === 'k') {
        e.preventDefault()
        setPaletteOpen(p => !p)
        return
      }
      if (meta && e.key === '\\') {
        e.preventDefault()
        setSelectedItem(selectedItem ? null : selectedItem)
        // Detail toggle: 選択中ならクリア、なければ何もしない
        if (selectedItem) setSelectedItem(null)
        return
      }
      if (meta && /^[0-8]$/.test(e.key)) {
        const idx = parseInt(e.key, 10)
        const target: LensId | undefined = LENS_ORDER[idx]
        if (target) {
          e.preventDefault()
          navigate(target)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate, selectedItem, setSelectedItem])

  // Header search → Explore Lens に遷移して検索
  // 値は useGlobalSearchQuery が共有しているので、ここでは検索確定 event を発火して navigate するだけ
  const handleHeaderSearch = useCallback((_q: string) => {
    navigate('explore')
    // 検索確定: Explore に再 fetch をさせる
    window.dispatchEvent(new CustomEvent('explore_filter_change'))
  }, [navigate])

  const handleResetLayout = useCallback(() => {
    resetDetailWidth()
    resetSubSidebarWidth()
  }, [resetDetailWidth, resetSubSidebarWidth])

  const sharedDetail = selectedItem ? (
    <SharedDetailPanel
      id={selectedItem.id}
      itemType={selectedItem.itemType}
      onClose={() => setSelectedItem(null)}
      onUnauthorized={onUnauthorized}
    />
  ) : (
    <div className="h-full flex items-center justify-center px-8 text-center text-sm text-text-subtle">
      <p>item を選択してください</p>
    </div>
  )

  const header = (
    <AppHeader
      onSearch={handleHeaderSearch}
      onResetLayout={viewport !== 'mobile' ? handleResetLayout : undefined}
      onLogout={onUnauthorized}
    />
  )

  // ===== Mobile =====
  if (viewport === 'mobile') {
    return (
      <>
        <MobileAppShell
          header={header}
          main={renderLensMain(lens)}
          bottomNav={<BottomNav />}
          detailModal={selectedItem ? (
            <MobileDetailModal onClose={() => setSelectedItem(null)}>
              <SharedDetailPanel
                id={selectedItem.id}
                itemType={selectedItem.itemType}
                onClose={() => setSelectedItem(null)}
                onUnauthorized={onUnauthorized}
              />
            </MobileDetailModal>
          ) : undefined}
        />
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onLogout={onUnauthorized} />
      </>
    )
  }

  // ===== Desktop / Tablet =====
  return (
    <>
      <AppShell
        header={header}
        lensNav={<LensNav />}
        subSidebar={renderLensSidebar(lens)}
        main={renderLensMain(lens)}
        detail={sharedDetail}
        detailVisible={detailVisible}
        detailWidth={detailWidth}
        onDetailResizeStart={onDragHandleMouseDown}
        subSidebarWidth={subSidebarWidth}
        onSubSidebarResizeStart={onSubSidebarResizeStart}
      />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onLogout={onUnauthorized}
      />
    </>
  )
}

function renderLensMain(lens: LensId) {
  switch (lens) {
    case 'home': return <HomeLens />
    case 'explore': return <ExploreLens />
    case 'reflect': return <ReflectLens />
    case 'judgment': return <JudgmentLens />
    case 'finance': return <FinanceLens />
    case 'health': return <HealthLens />
    case 'mind': return <MindLens />
    case 'collaborate': return <CollaborateLens />
    case 'graph': return <GraphLens />
  }
}

function renderLensSidebar(lens: LensId) {
  switch (lens) {
    case 'home': return null  // Home Sub-sidebar 廃止
    case 'explore': return <ExploreSidebar />
    case 'reflect': return <ReflectSidebar />
    case 'judgment': return <JudgmentSidebar />
    case 'finance': return <FinanceSidebar />
    case 'health': return <HealthSidebar />
    case 'mind': return <MindSidebar />
    case 'collaborate': return <CollaborateSidebar />
    case 'graph': return <GraphSidebar />
  }
}
